// Reine Layout-Mathematik des Kalender-Zeitrasters (Tag-/Wochen-Ansicht).
// BEWUSST in einem JSX-freien .ts-Modul, damit der Dry-Run
// (frontend/calendar-grid-dryrun.mts, `node --experimental-strip-types`) GENAU
// diese Funktionen importieren kann — kein Copy (Muster nextMeeting.ts ←
// x2-label-dryrun.mts). CalendarTimeGrid.tsx importiert von hier.
//
// Zeitform der Events: offset-lose naive Lokalzeit aus der Graph-Projektion
// ("2026-07-08T08:00:00.0000000") → `new Date(iso)` ist Lokalzeit. Alle
// Minutenwerte hier sind Minuten seit 00:00 des jeweiligen Tages.

/** Basis-Fenster des Rasters (Kerngeschäftszeit). Termine AUSSERHALB werden
 *  nicht versteckt — `hourRange` weitet das Fenster automatisch, bis der
 *  früheste/späteste Termin des Fensters drin liegt (kein Umschalter, kein
 *  abgeschnittener Termin). */
export const BASE_FROM_HOUR = 7;
export const BASE_TO_HOUR = 20;
/** Raster-Granularität für Klick/Ziehen im leeren Slot. */
export const SNAP_MIN = 15;
/** Dauer eines Klicks ohne Ziehen. */
export const DEFAULT_DURATION_MIN = 30;

const DAY_MIN = 24 * 60;

/** Minimal-Kontrakt, den das Raster von einem Event braucht. */
export interface GridEvent {
  start: string;
  end: string;
  is_all_day?: boolean | null;
}

/**
 * Startzeitpunkt als Zahl (ms seit Epoch). Unparsebares/fehlendes `start` →
 * `+Infinity`, damit solche Events ans ENDE sortieren statt über `NaN` eine
 * INTRANSITIVE Vergleichsfunktion zu erzeugen: `NaN`-Vergleiche sind immer
 * false, ein NaN-Comparator gäbe für jedes Paar mit einem kaputten Event 0
 * ("gleich") zurück — und die Reihenfolge der GÜLTIGEN Events hinge dann davon
 * ab, in welcher Reihenfolge der Sortier-Algorithmus die Paare zieht.
 */
export function startMs(ev: GridEvent): number {
  const t = Date.parse(ev.start);
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/**
 * Anzeige-Reihenfolge einer Termin-Liste: ganztägig zuerst, danach nach
 * ZEITPUNKT.
 *
 * Nicht `a.start.localeCompare(b.start)`: die ISO-Zeichenkette ordnet nur
 * zufällig richtig, solange ALLE Werte dasselbe Offset-Suffix tragen. Bei
 * gemischten Offsets kippt sie — `2026-07-27T09:00:00+02:00` (= 07:00Z) ist
 * FRÜHER als `2026-07-27T08:30:00Z`, als Text sortiert aber dahinter. Heute
 * liefert die Graph-Projektion einheitlich naive Lokalzeit; der Defekt wäre
 * also latent, würde aber beim ersten Formatwechsel still falsch anzeigen.
 *
 * Gleiche Zeitpunkte → 0; `Array.prototype.sort` ist stabil (ES2019), die
 * Eingabe-Reihenfolge bleibt dann erhalten (keine wechselnde Anzeige).
 */
export function compareEvents(a: GridEvent, b: GridEvent): number {
  const aAll = !!a.is_all_day;
  const bAll = !!b.is_all_day;
  if (aAll !== bAll) return aAll ? -1 : 1;
  const ta = startMs(a);
  const tb = startMs(b);
  // Bewusst kein `ta - tb`: Infinity - Infinity ergäbe NaN — also wieder
  // "gleich" für jedes Paar kaputter Events.
  return ta < tb ? -1 : ta > tb ? 1 : 0;
}

/** Kopie der Liste in Anzeige-Reihenfolge (mutiert die Eingabe nicht). */
export function sortEvents<T extends GridEvent>(events: T[]): T[] {
  return [...events].sort(compareEvents);
}

/** Ein auf EINEN Kalendertag geschnittenes Event-Stück. */
export interface DaySegment<T> {
  ev: T;
  /** Minuten seit 00:00 des Tages, auf [0, 1440] geklemmt. */
  startMin: number;
  endMin: number;
}

/** Segment mit Spalten-Zuweisung für nebeneinander liegende Überlappungen. */
export interface PlacedSegment<T> extends DaySegment<T> {
  /** 0-basierte Spalte innerhalb des Überlappungs-Clusters. */
  col: number;
  /** Spaltenzahl des Clusters (Breite = 1/colCount). */
  colCount: number;
}

export interface DayLayout<T> {
  day: Date;
  /** Ganztägig + Termine, die den kompletten Tag abdecken → Band über dem Raster. */
  allDay: T[];
  timed: PlacedSegment<T>[];
}

/**
 * Events auf einen Kalendertag schneiden. Ein Termin landet im Tag, wenn er ihn
 * überhaupt berührt (`end > dayStart && start < dayEnd`) — mehrtägige Termine
 * erscheinen dadurch in JEDEM berührten Tag mit dem passenden Stück.
 *
 * Ins `allDay`-Band gehen (a) `is_all_day`-Termine und (b) Stücke, die den Tag
 * exakt voll abdecken (00:00–24:00) — sonst würde ein mehrtägiger Termin das
 * Stunden-Fenster auf 0–24 aufblähen.
 */
export function splitDay<T extends GridEvent>(
  events: T[],
  dayStart: Date
): { allDay: T[]; timed: DaySegment<T>[] } {
  const dayStartMs = dayStart.getTime();
  const dayEndMs = dayStartMs + DAY_MIN * 60_000;
  const allDay: T[] = [];
  const timed: DaySegment<T>[] = [];
  for (const ev of events) {
    const s = new Date(ev.start).getTime();
    const e = new Date(ev.end).getTime();
    if (Number.isNaN(s) || Number.isNaN(e)) continue;
    if (e <= dayStartMs || s >= dayEndMs) continue; // Tag nicht berührt
    if (ev.is_all_day) {
      allDay.push(ev);
      continue;
    }
    const startMin = Math.max(0, Math.round((s - dayStartMs) / 60_000));
    const endMin = Math.min(DAY_MIN, Math.round((e - dayStartMs) / 60_000));
    if (startMin <= 0 && endMin >= DAY_MIN) {
      allDay.push(ev); // deckt den ganzen Tag → Band statt Raster
      continue;
    }
    // Null-Dauer-Termine bekommen 1 Minute, damit sie sichtbar/klickbar sind.
    timed.push({ ev, startMin, endMin: Math.max(endMin, startMin + 1) });
  }
  return { allDay, timed };
}

/**
 * Überlappende Segmente nebeneinander legen (Spalten-Packing, wie Outlook):
 * Segmente nach Start sortieren (längeres zuerst bei gleichem Start), in
 * Überlappungs-Cluster zerlegen (ein Cluster endet, sobald ein Segment nach dem
 * spätesten bisherigen Ende beginnt) und je Cluster greedy in die erste freie
 * Spalte setzen. Alle Segmente EINES Clusters teilen dieselbe `colCount`, damit
 * ihre Kanten aneinander ausgerichtet bleiben.
 */
export function packSegments<T>(segments: DaySegment<T>[]): PlacedSegment<T>[] {
  const sorted = [...segments].sort(
    (a, b) => a.startMin - b.startMin || b.endMin - a.endMin
  );
  const out: PlacedSegment<T>[] = [];
  let cluster: PlacedSegment<T>[] = [];
  let clusterEnd = -1;
  /** Letztes Ende je Spalte — nur innerhalb des aktuellen Clusters gültig. */
  let colEnds: number[] = [];

  const flush = () => {
    if (cluster.length) {
      const cols = Math.max(...cluster.map((c) => c.col)) + 1;
      for (const c of cluster) c.colCount = cols;
      out.push(...cluster);
    }
    cluster = [];
    clusterEnd = -1;
    colEnds = [];
  };

  for (const seg of sorted) {
    if (cluster.length && seg.startMin >= clusterEnd) flush();
    let col = colEnds.findIndex((end) => end <= seg.startMin);
    if (col === -1) col = colEnds.length;
    colEnds[col] = seg.endMin;
    cluster.push({ ...seg, col, colCount: 1 });
    clusterEnd = Math.max(clusterEnd, seg.endMin);
  }
  flush();
  return out;
}

/** Ein Tag komplett: schneiden + packen. */
export function layoutDay<T extends GridEvent>(
  events: T[],
  day: Date
): DayLayout<T> {
  const { allDay, timed } = splitDay(events, day);
  return { day, allDay, timed: packSegments(timed) };
}

/**
 * Gemeinsames Stunden-Fenster aller Spalten (EINE Zeitachse für die ganze
 * Woche). Basis 07–20; wird nach oben/unten so weit geweitet, dass jeder
 * Termin des geladenen Fensters vollständig im Raster liegt.
 */
export function hourRange<T>(
  layouts: DayLayout<T>[]
): { fromHour: number; toHour: number } {
  let fromHour = BASE_FROM_HOUR;
  let toHour = BASE_TO_HOUR;
  for (const l of layouts) {
    for (const seg of l.timed) {
      fromHour = Math.min(fromHour, Math.floor(seg.startMin / 60));
      toHour = Math.max(toHour, Math.ceil(seg.endMin / 60));
    }
  }
  return {
    fromHour: Math.max(0, Math.min(fromHour, BASE_FROM_HOUR)),
    toHour: Math.min(24, Math.max(toHour, fromHour + 1)),
  };
}

/** Geschäftszeiten-Fenster in ganzen Stunden — Form von
 *  `ui/calendarBusinessHours.ts::BusinessHours`. BEWUSST als eigener
 *  Minimal-Typ statt eines Imports: dieses Modul bleibt JSX- UND api-frei,
 *  damit der Dry-Run (`calendar-grid-dryrun.mts`) es laden kann. */
export interface HourWindow {
  start_hour: number;
  end_hour: number;
}

/**
 * Höhe der abgeblendeten Randflächen in STUNDEN — oben vor Arbeitsbeginn, unten
 * nach Arbeitsende. In Pixel rechnet erst der Aufrufer um: nur er kennt
 * `effHourPx` (die Stundenhöhe folgt der Containerhöhe).
 *
 * Warum die GESCHÄFTSZEITEN und nicht mehr `BASE_FROM_HOUR`/`BASE_TO_HOUR`: bis
 * hierher blendete das Raster ab, was außerhalb der GEZEICHNETEN Spanne 07-20
 * lag — also allein die Fläche, die `hourRange` wegen Rand-Terminen
 * hinzugeweitet hatte. Über den Arbeitstag des Nutzers sagte das nichts: bei
 * 9-17 standen 07-09 und 17-20 genauso hell da wie der Kern.
 *
 * Verworfen: beide Abblendungen übereinander stehen lassen. Zwei Overlays
 * ergeben drei Helligkeitsstufen (6-7 doppelt, 7-9 einfach, 9-17 hell) für eine
 * Information, die es nur einmal gibt. Die geweitete Fläche bleibt ohnehin
 * abgeblendet — sie liegt außerhalb der Geschäftszeiten.
 *
 * Die Fläche wird NICHT gesperrt (`pointer-events-none` am Overlay): der Klick
 * landet weiter in der Spalte. Termine außerhalb der Geschäftszeiten sind der
 * ausdrückliche Normalfall, sobald der Kunde die Zeit nennt.
 */
export function offHoursBands(
  fromHour: number,
  toHour: number,
  biz: HourWindow
): { topHours: number; bottomHours: number } {
  const span = Math.max(0, toHour - fromHour);
  const clamp = (h: number) => Math.min(Math.max(h, fromHour), toHour);
  const start = clamp(biz.start_hour);
  const end = clamp(biz.end_hour);
  // Kein Stück Geschäftszeit im gezeichneten Fenster. Über die Auswahlfelder
  // nicht erreichbar (Beginn ≤ 12, Ende ≥ 12, Fenster ⊇ 07-20), über einen von
  // Hand verbogenen `user_settings`-Eintrag schon. Dann ist die GANZE Fläche
  // Randzeit — als EIN Band, nicht als zwei: zwei würden sich überlappen und in
  // der Mitte die doppelte Deckkraft ergeben.
  if (end <= start) return { topHours: span, bottomHours: 0 };
  return { topHours: start - fromHour, bottomHours: toHour - end };
}

/** Auf das Raster runden (nearest, Standard 15 Min). */
export function snapMinutes(min: number, snap: number = SNAP_MIN): number {
  return Math.round(min / snap) * snap;
}

/**
 * Y-Position im Raster → Minute des Tages, gerundet aufs Raster und auf den
 * Tag geklemmt. `offsetY` = Pixel unterhalb der Oberkante der Rasterfläche.
 */
export function minuteAt(
  offsetY: number,
  hourPx: number,
  fromHour: number
): number {
  const raw = fromHour * 60 + (offsetY / hourPx) * 60;
  return Math.max(0, Math.min(DAY_MIN, snapMinutes(raw)));
}

/**
 * Aus einem Zieh-Bereich (from/to in Minuten, Reihenfolge beliebig) das
 * Termin-Fenster ableiten: aufs Raster gerundet; ein Klick ohne Ziehen (Länge 0)
 * wird zur Standard-Dauer. Das Ende bleibt im Tag.
 */
export function slotFromDrag(
  fromMin: number,
  toMin: number
): { startMin: number; endMin: number } {
  const a = snapMinutes(Math.min(fromMin, toMin));
  let b = snapMinutes(Math.max(fromMin, toMin));
  if (b - a < SNAP_MIN) b = a + DEFAULT_DURATION_MIN;
  const endMin = Math.min(DAY_MIN, b);
  // Am Tagesende nach hinten nicht mehr möglich → Start nach vorn ziehen.
  const startMin = Math.min(a, endMin - SNAP_MIN);
  return { startMin: Math.max(0, startMin), endMin };
}

/** Minute des Tages → echtes Date (Lokalzeit) auf Basis des Tagesanfangs. */
export function dateAtMinute(day: Date, min: number): Date {
  const d = new Date(day);
  d.setHours(0, 0, 0, 0);
  d.setMinutes(min);
  return d;
}
